import React, { useEffect, useState } from 'react';
import { AppLoadingSpinner } from './AppLoadingSpinner';

interface LoadingScreenProps {
  /** Called when the loading screen has fully hidden (so parent can show page content without glitch). */
  onTransitionComplete?: () => void;
}

const FADE_DURATION_MS = 400;
const MIN_DISPLAY_TIME_MS = 1500;

const LoadingScreen: React.FC<LoadingScreenProps> = ({ onTransitionComplete }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    const initialEl = document.getElementById('initial-loading');
    setIsLoading(true);
    setIsFadingOut(false);
    const startTime = Date.now();
    let didComplete = false;

    const showLoading = () => {
      if (didComplete) return;
      didComplete = true;
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_DISPLAY_TIME_MS - elapsed);

      setTimeout(() => {
        // Remove HTML loader only when we're ready to fade — prevents logo jump / navy flash during initial paint
        if (initialEl) initialEl.remove();
        onTransitionComplete?.(); // Reveal content before fade so we crossfade (no black flash)
        setIsFadingOut(true);
        setTimeout(() => {
          setIsLoading(false);
        }, FADE_DURATION_MS);
      }, remainingTime);
    };

    // Mobile fallback: load event can fire late or not at all; poll readyState and use timeout
    const FALLBACK_MS = 4000;
    const poll = setInterval(() => {
      if (document.readyState === 'complete') {
        clearInterval(poll);
        showLoading();
      }
    }, 100);

    const fallbackTimer = setTimeout(() => {
      clearInterval(poll);
      if (!didComplete) showLoading();
    }, FALLBACK_MS);

    const handleLoad = () => {
      clearInterval(poll);
      clearTimeout(fallbackTimer);
      showLoading();
    };

    if (document.readyState === 'complete') {
      clearInterval(poll);
      clearTimeout(fallbackTimer);
      showLoading();
    } else {
      window.addEventListener('load', handleLoad);
    }

    return () => {
      clearInterval(poll);
      clearTimeout(fallbackTimer);
      window.removeEventListener('load', handleLoad);
    };
  }, [onTransitionComplete]);

  if (!isLoading) return null;

  return (
    <div
      className="fixed inset-0 bg-black z-[99999] flex items-center justify-center"
      style={{
        opacity: isFadingOut ? 0 : 1,
        pointerEvents: isFadingOut ? 'none' : 'auto',
        transition: `opacity ${FADE_DURATION_MS}ms cubic-bezier(0.25, 0.1, 0.25, 1)`,
        transform: 'translate3d(0,0,0)',
        WebkitTransform: 'translate3d(0,0,0)',
        willChange: isFadingOut ? 'opacity' : 'auto',
        backfaceVisibility: 'hidden' as const,
        WebkitBackfaceVisibility: 'hidden' as const,
        contain: 'layout style paint',
      }}
    >
      <AppLoadingSpinner />
    </div>
  );
};

export default LoadingScreen;
